import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faUserMd, faMicroscope, faHospital
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/Surgical-Analytics-Process.png";
import vis from "../../assets/images/use-cases/Surgical-Analytics-Dashboard.png";

const SurgicalOutcomeAnalytics = () => {
    return (
        <>
            <SEO
                title="Surgical Outcome Analytics: AI Digital Twin for Healthcare | Nebulyx AI"
                description="Improve surgical outcomes by connecting equipment telemetry, EMR systems, and planning data. Deliver predictive insights for surgeons, maintenance teams, and hospital administrators."
                keywords="AI digital twin, surgical analytics, healthcare, EMR integration, predictive maintenance, surgical outcomes, hospital KPIs"
                canonicalUrl="https://nebulyx.ai/use-cases/surgical-outcome-analytics"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faUserMd}/>
                                            &nbsp;
                                            Healthcare
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;How can we improve surgical outcomes by connecting equipment, EMR, and planning data?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        A unified surgical analytics Twin that combines equipment telemetry, patient records, and procedure plans to deliver predictive insights for surgeons, maintenance teams, and hospital administrators.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faMicroscope} className="stat-icon"/>
                                <span className="stat-value">5</span>
                                <span className="stat-label">Data Sources Unified</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">Real-time</span>
                                <span className="stat-label">Predictive Insights</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faHospital} className="stat-icon"/>
                                <span className="stat-value">4</span>
                                <span className="stat-label">Stakeholder Dashboards</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>Surgical equipment data siloed from patient records</li>
                                            <li>No visibility linking procedure plans to actual outcomes</li>
                                            <li>Equipment maintenance reactive, not predictive</li>
                                            <li>Quality metrics disconnected from operational decisions</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Data Object Graph connects equipment, EMR, and planning systems</li>
                                            <li>Enriched surgical profiles combine quality and maintenance data</li>
                                            <li>Analytics engine generates predictive insights</li>
                                            <li>Role-based dashboards for surgeons, admin, and maintenance</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Surgeons receive predictive guidance during planning</li>
                                            <li>Hospital admins see real-time performance KPIs</li>
                                            <li>Maintenance teams get equipment failure predictions</li>
                                            <li>Outcome feedback loop improves future procedures</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Connects surgical equipment, hospital EMR, planning systems, quality management, and maintenance data into a unified Data Object Graph that powers predictive analytics across the surgical workflow.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Connect Sources</strong>
                                    <span>Equipment telemetry, EMR, planning, quality, maintenance systems</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Build Profiles</strong>
                                    <span>Surgical case profiles enriched with quality and maintenance data</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Generate Insights</strong>
                                    <span>Analytics engine produces KPIs and predictive models</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Deliver & Feedback</strong>
                                    <span>Role-based dashboards with outcome feedback to planning</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Ingests data from surgical equipment, hospital EMR, planning systems, quality management,
                                            and maintenance records. Builds enriched surgical profiles that feed an analytics engine,
                                            producing predictive insights for multiple stakeholder dashboards.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Surgical Analytics Dashboard</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Delivers role-based views for surgeons, hospital administrators, and maintenance teams.
                                            Surgeons see predictive guidance and outcome KPIs. Admins monitor operational performance.
                                            Maintenance teams receive equipment health predictions and service schedules.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>


                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Surgical Equipment Telemetry</li>
                                            <li>Hospital EMR Patient Records</li>
                                            <li>Surgical Planning Data</li>
                                            <li>Quality Management Metrics</li>
                                            <li>Equipment Maintenance Logs</li>
                                            <li>Procedure Outcomes</li>
                                            <li>Resource Utilisation Data</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Surgical case profile builder</li>
                                            <li>Enriched profile aggregator</li>
                                            <li>Performance KPI calculator</li>
                                            <li>Predictive outcome models</li>
                                            <li>Equipment failure predictor</li>
                                            <li>R&D insight generator</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Stakeholder Dashboards</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Surgeon Dashboard - KPIs & guidance</li>
                                            <li>Hospital Admin - operational metrics</li>
                                            <li>Predictive Maintenance Planner</li>
                                            <li>R&D Insights - product development</li>
                                            <li>Quality KPI Dashboard</li>
                                            <li>Outcome Feedback Loop</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Discovery</h3>
                                    <p>
                                        By modelling the surgical workflow as a <strong>Data Object Graph</strong>, we can connect
                                        previously siloed data sources into enriched surgical profiles.
                                        <strong> The analytics engine transforms this unified view into predictive insights-from
                                            outcome guidance for surgeons to maintenance predictions for equipment teams</strong>
                                        {" "}-while the feedback loop from outcomes back to planning enables continuous improvement
                                        across the entire surgical ecosystem.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Optimal Strategy Found</span>
                                        <span className="discovery-value">Unified Analytics</span>
                                        <span className="discovery-note">5 sources, 4 dashboards, 1 feedback loop</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Want to Unify Your Surgical Analytics?</h2>
                            <p>
                                If your equipment, EMR, and planning data are siloed and you're missing predictive insights,
                                let's build your surgical analytics Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default SurgicalOutcomeAnalytics;